import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import {
  MaterialOptionProps,
  PartyInfoProps,
  PartyOptionProps,
} from "../../types/materialLine";
import {
  MaterialOrderDateBy,
  MaterialOrderListProps,
  MaterialOrderProps,
  MaterialOrderSortBy,
  MaterialOrderSortOrder,
  MaterialOrderState,
} from "../../types/materialOrder";

export interface MaterialOrdersState {
  orders: MaterialOrderListProps[];
  totalOrders: number;
  currentOrder: MaterialOrderProps | null;
  currentOrderLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  materials: MaterialOptionProps[];
  materialsLoading: boolean;
  partyInfo: PartyInfoProps[];
  partyInfoRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: MaterialOrdersState = {
  orders: [],
  totalOrders: 0,
  currentOrder: null,
  currentOrderLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  materials: [],
  materialsLoading: false,
  partyInfo: [],
  partyInfoRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchMaterialOrderListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  status?: MaterialOrderState | null;
  date_by?: MaterialOrderDateBy;
  sort_by?: MaterialOrderSortBy;
  sort_order?: MaterialOrderSortOrder;
};

export const fetchMaterialOrderListData = createAsyncThunk(
  "fetchMaterialOrderListData",
  async (req: FetchMaterialOrderListDataProps) => {
    let response: any;
    await GET(`/v1/admin/material-orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchMaterialOrderDetail = createAsyncThunk(
  "fetchMaterialOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/material-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// Loads the open order again without clearing it first, so the page keeps showing it (no loader), e.g. after one of its
// purchases was deleted.
export const refreshMaterialOrderDetail = createAsyncThunk(
  "refreshMaterialOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/material-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// partyId keeps an edited order's own party in the list.
export const fetchMaterialOrderParties = createAsyncThunk(
  "fetchMaterialOrderParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/material-orders/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every materials party, active or not, and every party that has orders.
export const fetchMaterialOrderFilterParties = createAsyncThunk(
  "fetchMaterialOrderFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/material-orders/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchMaterialOrderMaterials = createAsyncThunk(
  "fetchMaterialOrderMaterials",
  async () => {
    let response: any;
    await GET(`/v1/admin/material-orders/materials`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchMaterialOrderPartyInfo = createAsyncThunk(
  "fetchMaterialOrderPartyInfo",
  async (req: { party_id: number; order_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/material-orders/party-info`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const materialOrdersSlice = createSlice({
  name: "materialOrders",
  initialState,
  reducers: {
    clearCurrentMaterialOrder: (state) => {
      state.currentOrder = null;
      state.currentOrderLoading = false;
    },
    clearMaterialOrderPartyInfo: (state) => {
      state.partyInfo = [];
      state.partyInfoRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMaterialOrderListData.pending, (state) => {
      state.orders = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchMaterialOrderListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.orders = action.payload.data.data.records;
          state.totalOrders = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.orders = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchMaterialOrderListData.rejected,
      (state, action: any) => {
        state.orders = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchMaterialOrderDetail.pending, (state) => {
      state.currentOrderLoading = true;
      state.currentOrder = null;
    });
    builder.addCase(fetchMaterialOrderDetail.fulfilled, (state, action: any) => {
      state.currentOrderLoading = false;
      if (action.payload.data.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });
    builder.addCase(fetchMaterialOrderDetail.rejected, (state) => {
      state.currentOrderLoading = false;
      state.currentOrder = null;
    });

    builder.addCase(refreshMaterialOrderDetail.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });

    builder.addCase(fetchMaterialOrderParties.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.partyOptions = action.payload.data.data.records.map(
          (party: any) => ({ value: party.id, label: partyText(party) })
        );
      }
    });

    builder.addCase(
      fetchMaterialOrderFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    builder.addCase(fetchMaterialOrderMaterials.pending, (state) => {
      state.materialsLoading = true;
    });
    builder.addCase(
      fetchMaterialOrderMaterials.fulfilled,
      (state, action: any) => {
        state.materialsLoading = false;
        if (action.payload.data.status === "success") {
          state.materials = action.payload.data.data.records;
        }
      }
    );
    builder.addCase(fetchMaterialOrderMaterials.rejected, (state) => {
      state.materialsLoading = false;
    });

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchMaterialOrderPartyInfo.pending, (state, action) => {
      state.partyInfo = [];
      state.partyInfoRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchMaterialOrderPartyInfo.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.partyInfoRequestId) return;
        state.partyInfo =
          action.payload.data.status === "success"
            ? action.payload.data.data.records
            : [];
      }
    );
  },
});

export const { clearCurrentMaterialOrder, clearMaterialOrderPartyInfo } =
  materialOrdersSlice.actions;

export const getMaterialOrders = (state: RootState) =>
  state.materialOrders.orders;
export const getTotalMaterialOrders = (state: RootState) =>
  state.materialOrders.totalOrders;
export const getCurrentMaterialOrder = (state: RootState) =>
  state.materialOrders.currentOrder;
export const getCurrentMaterialOrderLoading = (state: RootState) =>
  state.materialOrders.currentOrderLoading;
export const getMaterialOrderPartyOptions = (state: RootState) =>
  state.materialOrders.partyOptions;
export const getMaterialOrderFilterPartyOptions = (state: RootState) =>
  state.materialOrders.filterPartyOptions;
export const getMaterialOrderMaterials = (state: RootState) =>
  state.materialOrders.materials;
export const getMaterialOrderMaterialsLoading = (state: RootState) =>
  state.materialOrders.materialsLoading;
export const getMaterialOrderPartyInfo = (state: RootState) =>
  state.materialOrders.partyInfo;

export default materialOrdersSlice.reducer;
